import React, { lazy } from "react";
import { Route } from "react-router-dom";

// @material-ui/icons
import DashboardIcon from "@material-ui/icons/Dashboard";

import {
  mdiTextBoxPlusOutline,
  mdiFormatListBulletedSquare,
  mdiFormatListBulletedTriangle,
  mdiAccountPlusOutline,
  mdiAccountGroupOutline,
  mdiShapeOutline
} from "@mdi/js";

//Authorization
import Permissions, { PermissionActions } from "./permissions/Permissions";

//Auth Layout
const LoginPage = lazy(() => import("views/Auth/LoginPage.js"));
const ConfirmEmail = lazy(() => import("views/Auth/ConfirmEmail.js"));
const ForgotPassword = lazy(() => import("views/Auth/ForgotPassword.js"));
const ErrorPage = lazy(() => import("views/Auth/ErrorPage.js"));

//Home Layout
const Welcome = lazy(() => import('views/Home/Welcome'));
//Home//Assessments
const AssessmentList = lazy(() => import("views/Assessments/AssessmentList"));
const CreateAssessment = lazy(() => import("views/Assessments/CreateAssessment"));
const AssessmentDisabledList = lazy(() => import("views/Assessments/AssessmentDisabledList"));
const EditAssessment = lazy(() => import("views/Assessments/EditAssessment"));
//Home//Registration
const RegisterUser = lazy(() => import("views/Registration/RegisterUser"));
const UserList = lazy(() => import("views/Registration/UserList"));
//Home//Answers
const AnswerList = lazy(() => import("views/Answers/List"));
const AnswerQuestions = lazy(() => import("views/Answers/Questions"));
const AnswerReport = lazy(() => import("views/Answers/Report"));
//Home//Weights
const WeightList = lazy(() => import("views/Weights/WeightList"));

//Assessment Layout
const PreviewAssessment = lazy(() => import('views/Assessments/PreviewAssessment'));
const TakeAssessment = lazy(() => import('views/Assessments/TakeAssessment'));

const dashRoutes = [
  //AUTH LAYOUT
  {
    path: "/login",
    name: "Login Page",
    component: LoginPage,
    layout: "/auth",
    auth: false,
    permissions: [],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "/confirmemail",
    params: "/:token",
    name: "Confirm Email",
    component: ConfirmEmail,
    layout: "/auth",
    auth: false,
    permissions: [],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "/forgotpassword",
    name: "Forgot Password",
    component: ForgotPassword,
    layout: "/auth",
    auth: false,
    permissions: [],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "/error-page",
    name: "Error Page",
    component: ErrorPage,
    layout: "/auth",
    auth: false,
    permissions: [],
    allPermissions: false,
    sidebars: []
  },
  //HOME LAYOUT
  {
    path: "/welcome",
    name: "Home",
    icon: DashboardIcon,
    component: Welcome,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator, Permissions.Reader],
    allPermissions: false,
    sidebars: ['home']
  },
  {
    path: "/create-assessment",
    name: "Create Assessment",
    iconType: "mdi",
    icon: mdiTextBoxPlusOutline,
    component: CreateAssessment,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator],
    allPermissions: false,
    sidebars: ['home']
  },
  {
    path: "/assessments/list",
    name: "Assessment List",
    iconType: "mdi",
    icon: mdiFormatListBulletedSquare,
    component: AssessmentList,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator, Permissions.Reader],
    allPermissions: false,
    sidebars: ['home']
  },
  {
    path: "/assessments/disabled",
    name: "Disabled Assessments",
    iconType: "mdi",
    icon: mdiFormatListBulletedTriangle,
    component: AssessmentDisabledList,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator, Permissions.Reader],
    allPermissions: false,
    sidebars: ['home']
  },
  {
    path: "/assessments/edit",
    params: "/:id",
    name: "Edit Assessment",
    component: EditAssessment,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "/answers/list",
    params: "/:id",
    name: "Assessment Answers",
    component: AnswerList,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator, Permissions.Reader],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "/answers/questions",
    params: "/:id",
    name: "Answer Questions",
    component: AnswerQuestions,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator, Permissions.Reader],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "/weight/list",
    params: "/:disabled?",
    name: "Weights",
    iconType: "mdi",
    icon: mdiShapeOutline,
    component: WeightList,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator],
    allPermissions: false,
    sidebars: ['home']
  },
  {
    path: "/user/register",
    name: "Register Users",
    iconType: "mdi",
    icon: mdiAccountPlusOutline,
    component: RegisterUser,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator],
    allPermissions: false,
    sidebars: ['home']
  },
  {
    path: "/user/list",
    name: "User List",
    iconType: "mdi",
    icon: mdiAccountGroupOutline,
    component: UserList,
    layout: "/home",
    auth: true,
    permissions: [Permissions.Administrator],
    allPermissions: false,
    sidebars: ['home']
  },
  //ASSESSMENT LAYOUT
  {
    path: "/preview-assessment",
    params: "/:id",
    name: "Preview Assessment",
    component: PreviewAssessment,
    layout: "/a",
    auth: true,
    permissions: [Permissions.Administrator],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "",
    params: "/:id",
    name: "Take Assessment",
    component: TakeAssessment,
    layout: "/a",
    auth: false,
    permissions: [],
    allPermissions: false,
    sidebars: []
  },
  {
    path: "/report",
    params: "/:id",
    name: "Assessment Report",
    component: AnswerReport,
    layout: "/auth",
    auth: false,
    permissions: [],
    allPermissions: false,
    sidebars: []
  },
];

/**
* Returns all route objects that match the given parameters
* @param {{
 * layout: "Which layout to be fetched, all layouts if empty",
 * auth: "If the user is authenticated or not",
 * userPermissions: "Current user permission array",
 * }} Json Object
 */
const mapRoutes = ({ routes = dashRoutes, layout = '', auth = false, userPermissions = [] }) => {
  let result = routes.map((prop, key) => {
    //Don't return route if it requires auth but user doesn't have it
    if (!(!prop.auth || auth)) return <Route path={`${prop.layout}${(prop.path ? prop.path : '')}${(prop.params ? prop.params : '')}`} component={ErrorPage} key={key} />;
    //If the route requires permissions, check if the current user permissions contain the permissions required
    if (prop.permissions.length) {
      if (prop.allPermissions) {
        if (!PermissionActions.userHasAllPermissions(userPermissions, prop.permissions)) return <Route path={`${prop.layout}${(prop.path ? prop.path : '')}${(prop.params ? prop.params : '')}`} component={ErrorPage} key={key} />;
      }
      else if (!PermissionActions.userHasAnyPermissions(userPermissions, prop.permissions)) {
        return <Route path={`${prop.layout}${(prop.path ? prop.path : '')}${(prop.params ? prop.params : '')}`} component={ErrorPage} key={key} />;
      }
    }

    if (prop.collapse) {
      return mapRoutes({ layout: layout, routes: prop.views, auth: auth, userPermissions: userPermissions });
    }

    if (!layout || prop.layout === layout) return (
      <Route
        path={`${prop.layout}${(prop.path ? prop.path : '')}${(prop.params ? prop.params : '')}`}
        component={prop.component}
        key={key}
      />
    );
    else return <Route path={`${prop.layout}${(prop.path ? prop.path : '')}${(prop.params ? prop.params : '')}`} component={ErrorPage} key={key} />;
  });
  return result;
};

/**
* Returns all routes elements that match the given parameters
* @param {{
  * layout: "Which layout to be fetched, all layouts if empty",
  * auth: "If the user is authenticated or not",
  * userPermissions: "Current user permission array",
  * sidebar: "Which sidebar items should be fetched, all items if empty",
  * }} Json Object
  */
const filterRoutes = ({ layout = '', routes = dashRoutes, auth = false, userPermissions = [], sidebar = '' }) => {
  const filterRoutes = (routesToFilter) => {
    let result = [];
    for (let i = 0; i < routesToFilter.length; i++) {
      //Don't return route if it requires auth but user doesn't have it
      if (!(!routesToFilter[i].auth || auth)) continue;
      //If the route requires permissions, check if the current user permissions contain the permissions required
      if (routesToFilter[i].permissions.length) {
        if (routesToFilter[i].allPermissions) {
          if (!PermissionActions.userHasAllPermissions(userPermissions, routesToFilter[i].permissions)) continue;
        }
        else if (!PermissionActions.userHasAnyPermissions(userPermissions, routesToFilter[i].permissions)) continue;
      }

      //Check if current sidebar is included in the route list or no sidebar has been requested.
      if (!(!sidebar || routesToFilter[i].sidebars.includes(sidebar))) continue;

      if (routesToFilter[i].collapse) {
        let childRoutes = Object.create(routesToFilter[i]);
        childRoutes.views = filterRoutes(routesToFilter[i].views);
        result.push(childRoutes);
      }
      else {
        if (!(!layout || routesToFilter[i].layout === layout)) continue;
        result.push(Object.create(routesToFilter[i]));
      }
    }
    return result;
  };
  let result = filterRoutes(routes);
  return result;
};

const getActiveRoute = routes => {
  let activeRoute = "";
  for (let i = 0; i < routes.length; i++) {
    if (routes[i].collapse) {
      let collapseActiveRoute = getActiveRoute(routes[i].views);
      if (collapseActiveRoute !== activeRoute) {
        return collapseActiveRoute;
      }
    } else {
      if (
        window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].name;
      }
    }
  }
  return activeRoute;
};

export default dashRoutes;

export const routeActions = {
  mapRoutes: mapRoutes,
  filterRoutes: filterRoutes,
  getActiveRoute: getActiveRoute
};